import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";
import { type BookGroup, bookGroups } from "../../src/bookgroups.ts";
import { bookingOpens, tripStart } from "../../src/days.ts";
import type { Amount } from "../../src/money.ts";
import { booksFor, type ToBook, toBook as wantedToBook } from "../../src/tobook.ts";
import { dueSoonFor } from "../../src/waits.ts";
import { AddTodoForm } from "./AddTodo.tsx";
import type { Item, Reminder, TravelDocument, TripFile, TripView } from "./api.ts";
import { examplePrompt } from "./ask-ai.ts";
import { TravelDocuments } from "./Documents.tsx";
import {
  bookerFact,
  type ItemIconName,
  itemWhen,
  type Kind,
  kindClass,
  kindOf,
  localToday,
  personName,
  roundMoney,
  sentence,
  toBookWhy,
  weekday,
} from "./format.ts";
import { asLabel, everyone, membersNow } from "./former.tsx";
import { useFolds, useKeptFirst, useKeptScroll } from "./kept.ts";
import { ChoiceOptions, OtherOptions, toRate } from "./Options.tsx";
import { litProps, useLastOpened } from "./opened.ts";
import { ItemThumb } from "./Photos.tsx";
import { paidMoney, policyNote } from "./paid.ts";
import { type Choice, choices, ratingWaits } from "./plan.ts";
import { ReminderDialog, ReminderRow } from "./Reminders.tsx";
import { useReadOnly } from "./readOnly.ts";
import { keptKey } from "./reopen.ts";
import { navigate } from "./router.ts";
import { Button, Chip, Icon } from "./souvenir.ts";
import { FirstVisit } from "./Tips.tsx";
import { TodoRow, todoFacts } from "./Todos.tsx";
import {
  Empty,
  FoldHeading,
  FoldLine,
  factLine,
  Held,
  ItemIcon,
  KindFold,
  KindIcon,
  PageSection,
  Title,
  WaitingCard,
} from "./ui.tsx";
import { useViewing } from "./viewing.ts";

// Bookings and to-dos, laid out as Ideas is (DESIGN.md, Views, Bookings; the founder, 2026-10-23): Money at the
// top, then Waiting for you while anything waits on me (exactly what the tab counts), then three sections.
// Bookings: everything to decide, book, do or pay for, and what's booked, by kind (Flights, Stays, Getting
// around, Tickets and activities, Food and drink, Other), each row one thing in its state, in the order things
// happen; a choice still to decide is a row with its options folded under it, a decided one shows once, at its
// chosen option's row, its other options folded under it; what's cancelled comes last in its kind, with what
// came back. Reminders: what Tripmo will send. Travel documents. Money: what's been paid (less refunds
// received), what's booked but not paid yet and refunds on their way, per currency and by category, with an
// estimate in my currency, held against the budget. Kept simple on purpose: no shares, no settling up.

interface Props {
  trip: TripView;
  // What's in the plan (options left out until picked).
  items: Item[];
  // Everything in the trip, choices and options too: what's to book is worked out over all of it, the
  // same as on the server (a picked option is "chosen").
  all: Item[];
  reminders: Reminder[];
  documents: TravelDocument[];
  // Files on documents (only those this person may see).
  files: TripFile[];
  // From an upload link an AI gave before documents were the app's alone: the document to attach a file to.
  attach: string | null;
  onOpen: (id: string) => void;
  // An option's Show on map: the map with it selected, its choice's other options beside it.
  onShow: (id: string) => void;
  onChanged: () => void;
  // Its Add (AskAi.tsx) beside its title, on a computer; on a phone it floats above the tabs.
  add?: ReactNode;
  // "Add a to-do" chosen from its Add: the form opens at the foot of Bookings, scrolled to.
  start?: boolean;
  // "Add a travel document" chosen from its Add: its form opens at the foot of Travel documents.
  startDocument?: boolean;
  onStarted?: () => void;
}

const CATEGORY_LABEL = {
  stays: "Stays",
  transport: "Getting around",
  food: "Food",
  activities: "Things to do",
  other: "Other",
};

// One thing on Bookings, in its state (DESIGN.md, Bookings): a choice still to decide, something to book (with
// the to-do that books it), booked, a to-do, paid for along the way, or called off; and a choice's chosen option
// with nothing to book while its other options wait on my rating, so they're here to rate, as the tab counts
// them.
type Thing =
  | { state: "decide" | "chosen"; item: Item; choice: Choice }
  | { state: "book"; item: Item; booking: ToBook<Item> }
  | { state: "booked" | "todo" | "paid" | "cancelled"; item: Item };

// Each kind's head, drawn as a kind of Ideas' Suggestions: its icon on the colour of the map's kind it is.
const KIND_LOOK: Record<BookGroup, { kind: Kind; icon: ItemIconName }> = {
  flights: { kind: "around", icon: "plane" },
  stays: { kind: "stays", icon: "bed" },
  around: { kind: "around", icon: "route" },
  activities: { kind: "sights", icon: "ticket" },
  food: { kind: "food", icon: "meal" },
  other: { kind: "other", icon: "pin" },
};

// Open: still to decide, to book or to do. Something open in a kind keeps it open and says so on its chip.
const isOpen = (t: Thing) =>
  t.state === "decide" || t.state === "book" || (t.state === "todo" && t.item.status !== "done");

// What's open in a kind, on its chip: "1 to decide", "2 to book", "3 to do", two at most ("2 to book · 4 to
// do"), by a clock, as "to rate" is by its star: each of them waits on a time (when booking opens, by when it's
// due, the day it's for). With nothing open, "All booked" in moss with a tick, or "All done" where nothing in
// it is booked; with only what was cancelled, nothing.
const OPEN_WORDS = [
  ["decide", "to decide"],
  ["book", "to book"],
  ["todo", "to do"],
] as const;
function kindChip(rows: Thing[]): ReactNode {
  const open = OPEN_WORDS.map(([state, words]) => ({
    n: rows.filter((t) => t.state === state && isOpen(t)).length,
    words,
  })).filter(({ n }) => n > 0);
  if (open.length > 0)
    return (
      <Chip icon="clock">
        {open
          .slice(0, 2)
          .map(({ n, words }) => `${n} ${words}`)
          .join(" · ")}
      </Chip>
    );
  if (rows.length === 0) return null;
  return (
    <Chip icon="check" tone="moss">
      {rows.some((t) => t.state === "booked") ? "All booked" : "All done"}
    </Chip>
  );
}

// A state, as a row's line says it first (a to-do's own row says "To-do" or "Done", Todos.tsx). A decided
// choice's row in Waiting for you starts with its chosen option's ("Booked · Haeundae Centum Hotel · 2 to rate").
const STATE_WORD: Record<Thing["state"], string> = {
  decide: "To decide",
  book: "To book",
  booked: "Booked",
  todo: "To-do",
  paid: "Paid",
  chosen: "Chosen",
  cancelled: "Cancelled",
};

// Where each part is kept folded or open on this device, with the trip's other folds (`useFolds`).
const SECTION = "bookings:kinds";
const kindFold = (key: BookGroup) => `bookings:kind:${key}`;

type Note = { text: string; soon: boolean };

export function Bookings({
  trip,
  items,
  all,
  reminders,
  documents,
  files,
  attach,
  onOpen,
  onShow,
  onChanged,
  add: addButton = null,
  start: addAsked = false,
  startDocument = false,
  onStarted,
}: Props) {
  const start = tripStart(trip.dates);
  const list = useRef<HTMLElement>(null);
  useKeptScroll(list, "bookings");
  // The last thing opened from here, lit.
  const last = useLastOpened();
  const today = localToday();
  const inTrip = membersNow(trip);
  const byId = new Map(items.map((i) => [i.id, i]));
  // The sections, kinds, choices' options and cancelled lines, as I last left them on this device.
  const folds = useFolds(keptKey("folds", trip.you, trip.id));
  // What needs booking and is wanted, each with why (src/tobook.ts: the same rule as the booking
  // reminders and what an AI is told). An idea nobody wants stays out, "Needs booking" on its card.
  // Each as the plan has it (a picked option on its choice's day).
  const toBook = wantedToBook(
    all,
    trip.members.map((m) => m.id),
  ).map((t) => ({ ...t, item: byId.get(t.item.id) ?? t.item }));
  // A to-do that books something shows once, as that booking (until 2026-10-23 one due soon showed in Due
  // soon and again in To book): by the to-do, its booking.
  const bookedBy = new Map(toBook.flatMap((t) => (t.task ? [[t.task.id, t] as const] : [])));
  // The choices, in date order: one still to decide is a row of its own, its options folded under it; a
  // decided one shows at its chosen option's row, its other options folded under that.
  const decisions = choices(all, start);
  const decidedBy = new Map(decisions.flatMap((c) => (c.picked ? [[c.picked.id, c] as const] : [])));
  // How many of a choice's options wait on my rating (src/waits.ts; plan.ts's `ratingWaits`, as the tab
  // counts them).
  // None on a read-only trip, which takes no ratings.
  const readOnly = useReadOnly();
  // A shared trip's view (features/sharing.md): Money and Travel documents only with their switches, no Reminders.
  const viewing = useViewing();
  const ratingLeft = (c: Choice) =>
    readOnly
      ? 0
      : toRate(
          c.options.filter((o) => o.status === "option"),
          trip.you,
        );

  // Each thing once, in its strongest state.
  const things: Thing[] = [];
  const seen = new Set<string>();
  const add = (t: Thing) => {
    if (seen.has(t.item.id)) return;
    seen.add(t.item.id);
    things.push(t);
  };
  for (const c of decisions) if (!c.picked) add({ state: "decide", item: c.choice, choice: c });
  for (const booking of toBook) add({ state: "book", item: booking.item, booking });
  for (const i of items)
    if (i.status === "booked" || (i.type === "task" && i.booking_needed && i.status === "done"))
      add({ state: "booked", item: i });
  for (const i of items) if (i.type === "task" && !bookedBy.has(i.id)) add({ state: "todo", item: i });
  for (const i of items)
    if ((i.paid?.length || i.charges?.length) && i.status !== "cancelled") add({ state: "paid", item: i });
  for (const c of decisions)
    if (c.picked && !seen.has(c.picked.id) && ratingLeft(c) > 0)
      add({ state: "chosen", item: byId.get(c.picked.id) ?? c.picked, choice: c });
  // Called off: not on the map any more, kept here with what came back.
  for (const i of items) if (i.status === "cancelled") add({ state: "cancelled", item: i });
  // A to-do that booked something shows once, as that thing, done too (DESIGN.md, Bookings; the lead's call,
  // 2026-10-23: once booked, its done to-do was a second row beside it): a done to-do pointed at what it books
  // goes into that thing's row while the thing has one here. One still open stays a row of its own, to tick.
  const rowOf = new Set(things.map((t) => t.item.id));
  const shown = things.filter((t) => {
    const books = t.item.status === "done" ? booksFor(t.item, byId) : null;
    return !(books && rowOf.has(books));
  });
  // By kind, each in the order things happen (src/bookgroups.ts).
  const kinds = bookGroups(shown, (t) => t.item, all, start);
  const kindOfRow = new Map(kinds.flatMap((k) => k.rows.map((t) => [t.item.id, k.key] as const)));
  // The kinds with something open, and the choices with options waiting on my rating, when the page opened:
  // they stay open as I tick the last to-do or rate the last option, rather than folding under my finger; next
  // time the page opens, they start folded.
  const openFirst = useKeptFirst("bookings-kinds-open", () =>
    kinds.filter((k) => k.rows.some(isOpen)).map((k) => k.key as string),
  );
  const ratingFirst = useKeptFirst("bookings-options-rating", () =>
    decisions.filter((c) => ratingLeft(c) > 0).map((c) => c.choice.id),
  );

  // Waiting for you (DESIGN.md, "What waits on me leads"): exactly what the tab counts (TripPage), my to-dos
  // and bookings, mine or nobody's, due within 7 days or late, by date (on a day the important ones first),
  // then each choice whose options wait on my rating. Nothing on a read-only trip, where none can be ticked or
  // rated.
  const due = (readOnly ? [] : all)
    .filter((i) => dueSoonFor(i, trip.you, today, inTrip))
    .sort(
      (a, b) => (a.due ?? "").localeCompare(b.due ?? "") || Number(Boolean(b.important)) - Number(Boolean(a.important)),
    );
  const toRateHere = readOnly ? [] : ratingWaits(all, trip.you).options;
  const rating = decisions.filter((c) => toRateHere.some((o) => o.parent === c.choice.id));
  const waiting = due.length + toRateHere.length;
  // A choice there opens where it is in its kind: the section, its kind and its options unfolded, and scrolled
  // to, its row where the view's own top padding ends (on a phone, clear of the trip's bar over it).
  const [revealing, setRevealing] = useState<string | null>(null);
  useEffect(() => {
    const view = list.current;
    if (!revealing || !view) return;
    setRevealing(null);
    const options = document.getElementById(`options-${revealing}`) ?? document.getElementById(`others-${revealing}`);
    const at = options?.closest("li");
    if (!at) return;
    const top =
      view.scrollTop +
      at.getBoundingClientRect().top -
      view.getBoundingClientRect().top -
      Number.parseFloat(getComputedStyle(view).paddingTop);
    const still = matchMedia("(prefers-reduced-motion: reduce)").matches;
    view.scrollTo({ top, behavior: still ? "auto" : "smooth" });
  });
  const reveal = (c: Choice) => {
    const kind = kindOfRow.get(c.picked?.id ?? c.choice.id);
    folds.set(SECTION, false);
    if (kind) folds.set(kindFold(kind), false);
    folds.set(c.picked ? `others:${c.choice.id}` : `options:${c.choice.id}`, false);
    setRevealing(c.choice.id);
  };

  // A choice's row is drawn as its kind is (dinner between two restaurants, food; where we sleep, a stay).
  const lookOf = (c: Choice) => KIND_LOOK[kindOfRow.get(c.picked?.id ?? c.choice.id) ?? "stays"];
  const choiceIcon = (c: Choice) => (
    <span className={`tm-row-icon ${kindClass(lookOf(c).kind)}`}>
      <ItemIcon name={lookOf(c).icon} size={18} />
    </span>
  );

  const acts = { trip, onOpen, onShow, onChanged };
  // A row: its icon, its title, its state and facts on its second line, and a note under that (in the sun's
  // colour when it needs doing soon). It opens its card, or does `onClick`; what folds under it (`under`)
  // comes after it. Something called off is off the map: nothing to open.
  const row = (
    i: Item,
    line: ReactNode,
    {
      note,
      icon,
      kind = kindOf(i),
      onClick,
      under,
    }: { note?: Note | null; icon?: ReactNode; kind?: Kind; onClick?: () => void; under?: ReactNode } = {},
  ) => {
    const inner = (
      <>
        {icon ??
          // A to-do that's a booking isn't a place, nor of a kind: the booking's lock, neutral.
          (i.type === "task" ? (
            <span className="tm-row-icon">
              <Icon name="lock" size={18} />
            </span>
          ) : i.status === "cancelled" ? (
            <KindIcon item={i} />
          ) : (
            <ItemThumb item={i} trip={trip.id} />
          ))}
        <span className="tm-row-text">
          <span className="callout">
            <Title text={i.title} />
          </span>
          {line && <span className="caption tm-muted">{line}</span>}
          {note && (
            <span className={`caption ${note.soon ? "tm-sun-text" : "tm-muted"}`}>
              <Held text={note.text} />
            </span>
          )}
        </span>
      </>
    );
    return (
      <li key={i.id}>
        {i.status === "cancelled" ? (
          <div className="tm-row tm-row-static">{inner}</div>
        ) : (
          <button
            type="button"
            className={`tm-row${i.id === last ? ` is-lit ${kindClass(kind)}` : ""}`}
            {...litProps(i.id === last)}
            onClick={onClick ?? (() => onOpen(i.id))}
          >
            {inner}
          </button>
        )}
        {under}
      </li>
    );
  };
  // A decided choice, once: its chosen option's other options, under its row.
  const others = (i: Item) => {
    const c = decidedBy.get(i.id);
    if (!c) return null;
    const key = `others:${c.choice.id}`;
    return <OtherOptions c={c} folded={folds.folded(key, true)} onFold={(f) => folds.set(key, f)} {...acts} />;
  };
  // Each row's second line, its state first (DESIGN.md, Bookings).
  const decideLine = (c: Choice) => {
    const n = c.options.length;
    const left = ratingLeft(c);
    return facts([
      STATE_WORD.decide,
      n === 0 ? "no options yet" : `${n} ${n === 1 ? "option" : "options"}`,
      left > 0 ? <span key="rate" className="tm-sun-text">{`${left} to rate`}</span> : null,
    ]);
  };
  // Something to book: its to-do, when it has one, said as a to-do's row says it, right after the state
  // ("To book · Important · nobody's yet · by 25 Oct · in 2 days · booking opens 25 Oct": the lead's call,
  // 2026-10-23; it said "to-do, nobody's yet, by 25 Oct"), how far its due date is right after that date,
  // never after another date on the line (DESIGN.md, "What waits on me leads"); then why it's to book, when,
  // when booking opens, and who books it, only when that's someone other than the to-do's owner.
  const bookLine = ({ item: i, why, task }: ToBook<Item>) => {
    const opens = bookingOpens(i.books_from, i.when, start);
    return facts([
      STATE_WORD.book,
      ...(task ? todoFacts(task, trip, today) : []),
      toBookWhy(why, i, trip.members, trip.you),
      why.kind === "day" ? null : itemWhen(i.when),
      opens ? (opens > today ? `booking opens ${fmtDay(opens)}` : "booking open now") : null,
      // Who books it: its booker, else the to-do's owner; one who has left the trip is nobody.
      bookerFact(i, task, inTrip, trip.members, trip.you),
    ]);
  };
  const thingRow = (t: Thing): ReactNode => {
    const i = t.item;
    switch (t.state) {
      case "decide": {
        const key = `options:${i.id}`;
        return row(i, decideLine(t.choice), {
          icon: choiceIcon(t.choice),
          kind: lookOf(t.choice).kind,
          under: (
            <ChoiceOptions
              c={t.choice}
              folded={folds.folded(key, ratingLeft(t.choice) === 0 && !ratingFirst.includes(i.id))}
              onFold={(f) => folds.set(key, f)}
              {...acts}
            />
          ),
        });
      }
      case "book":
        return row(i, bookLine(t.booking), { under: others(i) });
      case "booked":
        return row(
          i,
          facts([
            STATE_WORD.booked,
            itemWhen(i.when),
            [i.booking?.provider, i.booking?.ref].filter(Boolean).join(" "),
            bookedMoney(i, trip),
          ]),
          { note: policyNote(i), under: others(i) },
        );
      case "chosen":
        return row(i, facts([i.status === "done" ? "Done" : STATE_WORD.chosen, itemWhen(i.when)]), {
          under: others(i),
        });
      case "paid":
        // Only charged later so far: not paid yet, so it doesn't say so.
        return row(i, facts([i.paid?.length ? STATE_WORD.paid : "Charged later", paidBy(i, trip)]));
      case "todo":
        return <TodoRow key={i.id} item={i} trip={trip} onOpen={onOpen} state />;
      case "cancelled":
        return row(
          i,
          facts([
            i.booking?.cancelled_on
              ? `${STATE_WORD.cancelled} ${fmtDay(i.booking.cancelled_on)}`
              : STATE_WORD.cancelled,
            refundLine(i, trip),
          ]),
        );
    }
  };

  // Each section folds by its heading, as Ideas' do, open until I fold it.
  const section = (name: string) => {
    const key = `bookings:${name}`;
    const open = !folds.folded(key, false);
    return { open, onToggle: () => folds.set(key, open) };
  };
  const coming = reminders.filter((r) => !r.sent_at && !r.missed_at);
  // A reminder opened from its row (Reminders.tsx), while it's still to come: removed, sent, or gone once the page
  // loads again, it closes.
  const [reminderOpen, setReminderOpen] = useState<string | null>(null);
  const openReminder = coming.find((r) => r.id === reminderOpen) ?? null;
  // Add a travel document from Add: its section opened first, for its form at the foot of it (Documents.tsx).
  // biome-ignore lint/correctness/useExhaustiveDependencies: once for each Add a travel document asked from Add
  useEffect(() => {
    if (startDocument) folds.set("bookings:documents", false);
  }, [startDocument]);
  // Add a to-do (AddTodo.tsx), at the foot of Bookings, where its to-dos are; from Add, the section opened first.
  const [adding, setAdding] = useState(false);
  const addAt = useRef<HTMLDivElement>(null);
  const [refocusAdd, setRefocusAdd] = useState(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: once for each Add a to-do asked from Add
  useEffect(() => {
    if (!addAsked) return;
    folds.set("bookings:kinds", false);
    setAdding(true);
    // Once it's drawn, scrolled to (clear of the floating Add on a phone), its first field taking the focus.
    const frame = requestAnimationFrame(() => {
      const form = addAt.current?.querySelector<HTMLElement>(".tm-add-todo");
      form?.scrollIntoView({ block: "nearest" });
      form?.querySelector<HTMLElement>("input")?.focus({ preventScroll: true });
      onStarted?.();
    });
    return () => cancelAnimationFrame(frame);
  }, [addAsked]);
  useEffect(() => {
    if (!refocusAdd || adding) return;
    setRefocusAdd(false);
    addAt.current?.querySelector<HTMLElement>("button")?.focus({ preventScroll: true });
  }, [refocusAdd, adding]);
  const addTodo = readOnly ? null : (
    <div className="tm-add-todo-at" ref={addAt}>
      {adding ? (
        <AddTodoForm
          trip={trip}
          onChanged={onChanged}
          onClose={() => {
            setAdding(false);
            setRefocusAdd(true);
          }}
        />
      ) : (
        <Button variant="secondary" size="sm" icon="plus" data-edit="add-todo" onClick={() => setAdding(true)}>
          Add a to-do
        </Button>
      )}
    </div>
  );
  return (
    <section className="tm-bookings" aria-labelledby="bookings-title" ref={list}>
      <div className="tm-view-title tm-bookings-title">
        <h1 id="bookings-title" className="display-m">
          {viewing && !viewing.shares.has("todos") ? "Bookings" : "Bookings and to-dos"}
        </h1>
        {addButton}
      </div>
      <FirstVisit view="bookings" />
      {/* Money at the top (DESIGN.md, Bookings): on a phone first under the title, above every list; on a
          computer the top of its own column beside them (placed there by the grid, whatever comes first). */}
      {(!viewing || viewing.shares.has("money")) && <Money trip={trip} folds={folds} onOpen={onOpen} />}
      <div className="tm-bookings-lists">
        {waiting > 0 && (
          <WaitingCard count={waiting} line={waitingLine(due.length, toRateHere.length)}>
            {/* Each row as it is in its kind; a choice's opens its options there. */}
            <ul className="tm-rows tm-waiting-rows" aria-label="What waits">
              {due.map((t) => {
                const booking = bookedBy.get(t.id);
                return booking ? (
                  row(booking.item, bookLine(booking))
                ) : (
                  <TodoRow key={t.id} item={byId.get(t.id) ?? t} trip={trip} onOpen={onOpen} state />
                );
              })}
              {rating.map((c) =>
                row(
                  c.choice,
                  c.picked
                    ? facts([
                        STATE_WORD[things.find((t) => t.item.id === c.picked?.id)?.state ?? "chosen"],
                        c.picked.title,
                        <span key="rate" className="tm-sun-text">{`${ratingLeft(c)} to rate`}</span>,
                      ])
                    : decideLine(c),
                  { icon: choiceIcon(c), kind: lookOf(c).kind, onClick: () => reveal(c) },
                ),
              )}
            </ul>
          </WaitingCard>
        )}
        <PageSection
          id="bookings-kinds"
          title="Bookings"
          caption="What's to decide, book or do, and what's booked, by kind, each in the order it happens."
          {...section("kinds")}
        >
          {kinds.length === 0 ? (
            <Empty prompt={examplePrompt(trip.title, "bookingsKinds")} readOnly="Nothing to decide, book or do.">
              Nothing to decide, book or do yet. What needs booking shows here once someone wants it or it's in the
              plan, with who's booking it and by when; once it's booked, Mark as booked on its card, or send your AI the
              confirmation.
            </Empty>
          ) : null}
          {kinds.length > 0 &&
            kinds.map((k) => {
              const live = k.rows.filter((t) => t.state !== "cancelled");
              const off = k.rows.filter((t) => t.state === "cancelled");
              const id = `bookings-${k.key}`;
              const offKey = `bookings:cancelled:${k.key}`;
              const offOpen = !folds.folded(offKey, true);
              return (
                <KindFold
                  key={k.key}
                  id={id}
                  kind={KIND_LOOK[k.key].kind}
                  icon={KIND_LOOK[k.key].icon}
                  label={k.label}
                  count={live.length}
                  chip={kindChip(live)}
                  folded={folds.folded(kindFold(k.key), !live.some(isOpen) && !openFirst.includes(k.key))}
                  onFold={(f) => folds.set(kindFold(k.key), f)}
                >
                  {live.length > 0 && <ul className="tm-rows">{live.map(thingRow)}</ul>}
                  {/* What's cancelled, last, folded until opened: beside what replaced it, it read as two
                      stays a night (the lead's call, 2026-10-23). */}
                  {off.length > 0 && (
                    <div className="tm-cancelled">
                      <FoldLine open={offOpen} controls={`${id}-cancelled`} onToggle={() => folds.set(offKey, offOpen)}>
                        {`${off.length} cancelled`}
                      </FoldLine>
                      {offOpen && (
                        <ul id={`${id}-cancelled`} className="tm-rows">
                          {off.map(thingRow)}
                        </ul>
                      )}
                    </div>
                  )}
                </KindFold>
              );
            })}
          {addTodo}
        </PageSection>
        {!viewing && (
          <PageSection
            id="reminders"
            title="Reminders"
            count={coming.length}
            caption="What Tripmo will send, when, and to whom."
            {...section("reminders")}
          >
            {coming.length === 0 ? (
              <Empty prompt={examplePrompt(trip.title, "reminders")} readOnly="No reminders.">
                No reminders yet. Ask your AI to remind you, and Tripmo emails you on the day.
              </Empty>
            ) : (
              <ul className="tm-rows">
                {coming.map((r) => (
                  <ReminderRow key={r.id} reminder={r} trip={trip} onOpen={setReminderOpen} />
                ))}
              </ul>
            )}
          </PageSection>
        )}
        {(!viewing || viewing.shares.has("documents")) && (
          <PageSection
            id="documents"
            title="Travel documents"
            count={documents.length}
            caption="Yours in full; everyone else's status only, unless they share theirs with you."
            {...section("documents")}
          >
            {documents.length === 0 && (
              <Empty readOnly="No documents saved.">
                No documents saved yet. Add your passport, visas and insurance here: the trip sees their status and
                expiry, and only you and those you share them with see their details. No AI sees them.
              </Empty>
            )}
            <TravelDocuments
              trip={trip}
              documents={documents}
              files={files}
              attach={attach}
              start={startDocument}
              onStarted={onStarted}
              onChanged={onChanged}
            />
          </PageSection>
        )}
      </div>
      {openReminder && (
        <ReminderDialog
          reminder={openReminder}
          trip={trip}
          onOpenItem={openReminder.about && all.some((i) => i.id === openReminder.about?.id) ? onOpen : null}
          onChanged={onChanged}
          onClose={() => setReminderOpen(null)}
        />
      )}
    </section>
  );
}

type Folds = ReturnType<typeof useFolds>;

// What Waiting for you holds, in a line: "One to-do is due this week or late, and 2 options wait for your
// rating."
function waitingLine(due: number, rate: number): string {
  const todos = due === 1 ? "One to-do is due this week or late" : `${due} to-dos are due this week or late`;
  const options = rate === 1 ? "one option waits for your rating" : `${rate} options wait for your rating`;
  if (rate === 0) return `${todos}: yours, or nobody's yet.`;
  if (due === 0) return `${sentence(options)}, so your group can decide.`;
  return `${todos}, and ${options}.`;
}

// The facts on a row's second line, " · " between them (factLine), the first with a capital. A short one
// never breaks inside, so on a phone "Agoda AG-1128" or "Sat 28 Nov – Tue 1 Dec" stays whole on its line
// (reported 2026-10-17: a booking's reference broke at its hyphen); a long one between its words, never inside
// a reference, a date or an amount ("3906-5167-1399 / 3906-5168-9376" broke after "3906-" on the founder's
// Nagano hotel, 2026-10-23).
function facts(parts: (ReactNode | null | undefined)[]): ReactNode {
  const shown = parts.filter((p): p is NonNullable<ReactNode> => Boolean(p));
  if (!shown.length) return null;
  return factLine(shown.map((fact, k) => (k === 0 && typeof fact === "string" ? sentence(fact) : fact)));
}

function Money({ trip, folds, onOpen }: { trip: TripView; folds: Folds; onOpen: (id: string) => void }) {
  const { paid, refunded, refunds_due, booked_unpaid, by_category, budget, estimate } = trip.money;
  // Charged later (features/bookings-and-costs.md, "Charged later"): none in an answer kept offline from before.
  const coming = trip.money.coming_up ?? [];
  const charges = trip.money.charges ?? [];
  // What's coming up in my currency: none without an estimate (DESIGN.md, the log row "Money without an estimate":
  // counted as 0, it drew "About …, charged to cards later" with no currency and Bookings fell over); 0 from an
  // estimate kept offline from before Tripmo had charges.
  const comingMine = !estimate ? null : estimate.coming_up === undefined ? 0 : estimate.coming_up;
  const groupBudget =
    estimate?.budget != null && budget ? estimate.budget * (budget.per === "person" ? trip.members.length : 1) : null;
  // Only ever said beside an estimate, in its currency.
  const mine = estimate?.currency ?? null;
  const inMine = (amount: number) => roundMoney(amount, mine);
  // What's committed: paid, coming up, and booked but not paid yet. All known in my currency, or no bar.
  const known = estimate?.paid != null && estimate.booked_unpaid != null && comingMine !== null;
  const toPay = (estimate?.booked_unpaid ?? 0) + (comingMine ?? 0);
  const committed = known ? (estimate.paid ?? 0) + toPay : null;
  const anything =
    paid.length > 0 || coming.length > 0 || booked_unpaid.length > 0 || refunded.length > 0 || refunds_due.length > 0;
  // It folds by its heading as the lists' sections do, open until folded.
  const open = !folds.folded("bookings:money", false);
  return (
    <aside className="tm-money" aria-labelledby="money-title">
      <FoldHeading
        id="money-title"
        open={open}
        controls="money-title-body"
        onToggle={() => folds.set("bookings:money", open)}
      >
        Money
      </FoldHeading>
      {open && (
        <div id="money-title-body" className="tm-section-body">
          {!anything ? (
            <Empty prompt={examplePrompt(trip.title, budget ? "bookings" : "money")} readOnly="Nothing paid or booked.">
              Nothing paid or booked yet. What's paid and what's still to pay adds up here as bookings and payments are
              recorded: Mark as booked or Add a payment on a card, or give your AI the confirmation.
            </Empty>
          ) : (
            <>
              <div className="tm-money-part">
                <span className="eyebrow tm-muted">Paid</span>
                {paid.length ? (
                  <>
                    <p className="display-m tm-money-total">{paid.map(money).join(" + ")}</p>
                    {estimate?.paid != null && (
                      <p className="callout">
                        About <strong>{inMine(estimate.paid)}</strong> at {fmtDay(estimate.rates_date)}'s rates
                      </p>
                    )}
                  </>
                ) : (
                  <p className="callout tm-muted">Nothing paid yet.</p>
                )}
                {refunded.length > 0 && (
                  <p className="caption tm-muted">After {refunded.map(money).join(" + ")} back</p>
                )}
              </div>
              {coming.length > 0 && (
                <div className="tm-money-part">
                  <span className="eyebrow tm-muted">Coming up</span>
                  <p className="display-m tm-money-total">{coming.map(money).join(" + ")}</p>
                  {comingMine != null && (
                    <p className="callout">
                      About <strong>{inMine(comingMine)}</strong>, charged to cards later
                    </p>
                  )}
                  {charges.length > 0 && (
                    <ul className="tm-coming" aria-label="Charged later, by date">
                      {charges.map((c) => (
                        <li key={`${c.item} ${c.on} ${c.by} ${c.amount} ${c.currency}`}>
                          <button type="button" className="tm-coming-row" onClick={() => onOpen(c.item)}>
                            <span className="caption tm-muted tm-coming-day">{weekday(c.on)}</span>
                            <span className="callout tm-coming-what">{c.title}</span>
                            <span className="callout tm-coming-amount">{paidMoney(c.amount, c.currency)}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
              {booked_unpaid.length > 0 && (
                <div className="tm-money-part">
                  <span className="eyebrow tm-muted">Booked, not paid yet</span>
                  <p className="display-m tm-money-total">{booked_unpaid.map(money).join(" + ")}</p>
                  {estimate?.booked_unpaid != null && (
                    <p className="callout">
                      About <strong>{inMine(estimate.booked_unpaid)}</strong>, to pay later
                    </p>
                  )}
                </div>
              )}
              {refunds_due.length > 0 && (
                <div className="tm-money-part">
                  <span className="eyebrow tm-muted">Refunds on the way</span>
                  <p className="display-m tm-money-total">{refunds_due.map(money).join(" + ")}</p>
                  {estimate?.refunds_due != null && (
                    <p className="callout">
                      About <strong>{inMine(estimate.refunds_due)}</strong>, not taken off yet
                    </p>
                  )}
                </div>
              )}
            </>
          )}
          {budget && (
            <div className="tm-budget">
              <div className="tm-budget-line">
                <span className="callout">Budget</span>
                <span className="callout">
                  {money({ currency: budget.currency, amount: budget.amount })}
                  {budget.per === "person" ? " each" : " for the group"}
                </span>
              </div>
              {committed !== null && groupBudget && estimate && (
                <>
                  <BudgetBar paid={estimate.paid ?? 0} booked={toPay} budget={groupBudget} />
                  <p className="caption tm-muted">
                    About {inMine(committed)} of {inMine(groupBudget)}
                    {budget.per === "person" ? ` for ${trip.members.length}` : ""}
                  </p>
                  {toPay > 0 && (
                    <ul className="tm-budget-key caption tm-muted" aria-label="The bar">
                      {(estimate.paid ?? 0) > 0 && (
                        <li>
                          <span className="tm-key-paid" aria-hidden="true" />
                          {inMine(estimate.paid ?? 0)} paid
                        </li>
                      )}
                      <li>
                        <span className="tm-key-booked" aria-hidden="true" />
                        {inMine(toPay)}{" "}
                        {(comingMine ?? 0) === 0
                          ? "booked, not paid yet"
                          : (estimate.booked_unpaid ?? 0) === 0
                            ? "coming up"
                            : "coming up or booked, not paid yet"}
                      </li>
                    </ul>
                  )}
                </>
              )}
              {budget.notes && <p className="caption tm-muted">{budget.notes}</p>}
            </div>
          )}
          {estimate && anything && estimate.paid === null && estimate.booked_unpaid === null && (
            <p className="caption tm-muted">
              No daily rate for {estimate.currency} at the European Central Bank, so no estimate in it.
            </p>
          )}
          {anything && <WhereItGoes byCategory={by_category} estimate={estimate} />}
          {anything && <MoneyCurrency trip={trip} />}
        </div>
      )}
    </aside>
  );
}

// Where it goes: one stacked bar of what's committed (paid and booked) by category, in my currency,
// with a key of each category's amount, share, and its amounts as recorded. Without my currency the
// amounts can't be added up across currencies: the key alone, as recorded.
const CATEGORY_ORDER = ["stays", "transport", "food", "activities", "other"] as const;
function WhereItGoes({
  byCategory,
  estimate,
}: {
  byCategory: TripView["money"]["by_category"];
  estimate: TripView["money"]["estimate"];
}) {
  const inMine = (amount: number) => roundMoney(amount, estimate?.currency);
  const mine = CATEGORY_ORDER.map((key) => ({ key, amount: estimate?.by_category?.[key] ?? null }));
  const known = estimate !== null && mine.every((c) => c.amount !== null || byCategory[c.key].length === 0);
  const total = known ? mine.reduce((sum, c) => sum + (c.amount ?? 0), 0) : 0;
  const share = (amount: number | null) => (total > 0 && amount ? Math.round((100 * amount) / total) : 0);
  return (
    <section className="tm-where" aria-labelledby="where-title">
      <h3 id="where-title" className="callout tm-where-title">
        Where it goes{total > 0 && <span className="tm-muted"> · about {inMine(total)}</span>}
      </h3>
      {total > 0 && (
        <div className="tm-where-bar" role="img" aria-label="Spending by category">
          {mine
            .filter((c) => (c.amount ?? 0) > 0)
            .map((c) => (
              <span key={c.key} className={`tm-where-part is-${c.key}`} style={{ flexGrow: c.amount ?? 0 }} />
            ))}
        </div>
      )}
      <ul className="tm-categories" aria-label="By category, paid and booked">
        {mine.map((c) => {
          const recorded = byCategory[c.key];
          return (
            <li key={c.key} className="callout">
              <span className="tm-where-name">
                <span className={`tm-where-swatch is-${c.key}`} aria-hidden="true" />
                {CATEGORY_LABEL[c.key]}
              </span>
              <span className={`tm-where-amount${recorded.length ? "" : " tm-muted"}`}>
                {!recorded.length
                  ? "–"
                  : total > 0 && c.amount !== null
                    ? `${inMine(c.amount)} · ${share(c.amount)}%`
                    : recorded.map(money).join(" + ")}
                {recorded.length > 0 &&
                  total > 0 &&
                  !(recorded.length === 1 && recorded[0]?.currency === estimate?.currency) && (
                    <span className="caption tm-muted tm-where-recorded">{recorded.map(money).join(" + ")}</span>
                  )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// What's committed of the budget (past it, the bar is full): the paid part solid, the booked part
// hatched, drawn on the meter's own bar.
function BudgetBar({ paid, booked, budget }: { paid: number; booked: number; budget: number }) {
  const committed = paid + booked;
  const split = committed > 0 ? (100 * paid) / committed : 100;
  return (
    <meter
      className="tm-budget-bar"
      min={0}
      max={budget}
      value={Math.min(committed, budget)}
      aria-label="Committed of the budget"
      style={{ "--paid-split": `${split}%` } as CSSProperties}
    />
  );
}

const money = (a: Amount) => roundMoney(a.amount, a.currency);

const DAY = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "UTC" });
const fmtDay = (date: string) => DAY.format(new Date(`${date}T00:00:00Z`));

// Who paid what, each payer once, their payments together: "You paid SGD 609.93 + SGD 7.31, Mei paid ¥4,000".
function paidLine(i: Item, trip: TripView): string | null {
  if (!i.paid?.length) return null;
  return byPayer(i, trip)
    .map(([who, amounts]) => `${sentence(asLabel(who))} paid ${amounts.join(" + ")}`)
    .join(", ");
}
function byPayer(i: Item, trip: TripView): [string, string[]][] {
  const out = new Map<string, string[]>();
  for (const p of i.paid ?? []) {
    const who = personName(p.by, everyone(trip), trip.you) ?? "someone";
    out.set(who, [...(out.get(who) ?? []), paidMoney(p.amount, p.currency)]);
  }
  return [...out];
}

// What was paid along the way, and by whom, to the cent as recorded: "¥4,000 by Mei", "SGD 7.31 by you"; then what's
// charged later.
function paidBy(i: Item, trip: TripView): string | null {
  const paid = i.paid?.length
    ? byPayer(i, trip)
        .map(([who, amounts]) => `${amounts.join(" + ")} by ${who === "You" ? "you" : who}`)
        .join(", ")
    : null;
  return [paid, chargedLine(i)].filter(Boolean).join(", ") || null;
}

// What's charged later on it, by day: "SGD 600 charged Sun 1 Nov".
function chargedLine(i: Item): string | null {
  if (!i.charges?.length) return null;
  return i.charges.map((c) => `${paidMoney(c.amount, c.currency)} charged ${weekday(c.on)}`).join(", ");
}

// A booked thing's money: who paid what and what's charged later, or its price still to pay.
function bookedMoney(i: Item, trip: TripView): string | null {
  const paid = [paidLine(i, trip), chargedLine(i)].filter(Boolean).join(", ");
  if (paid || !i.cost) return paid || null;
  // A price per person is for the people it's booked for, else everyone (as the totals count it).
  const people = i.cost.per === "person" ? i.booking?.for?.length || trip.members.length : 1;
  const total = money({ currency: i.cost.currency, amount: i.cost.amount * people });
  return `${total}${people > 1 ? ` for ${people}` : ""}, not paid yet`;
}

// What came back from a cancelled booking: each refund, landed or on its way, or that nothing did.
function refundLine(i: Item, trip: TripView): string | null {
  if (!i.refunds) return i.paid?.length ? "no refund recorded" : null;
  if (i.refunds.length === 0) return "nothing back";
  return i.refunds
    .map((r) => {
      const who = personName(r.to, everyone(trip), trip.you) ?? "someone";
      const whom = who === "You" ? "you" : who;
      return r.received ? `${money(r)} back to ${whom}` : `${money(r)} on its way back to ${whom}`;
    })
    .join(", ");
}

// Which currency Money's estimates are in, and where it's from (DESIGN.md section 3, the person; src/currency.ts): my
// account's, chosen on the profile; this trip's own, set through my AI for it alone; or, with neither, the budget's.
// With none at all, where to choose it. Nothing said where the rates don't cover it (Money says so already), where
// there are no rates at all just now (mine is there, the estimate isn't), or on an answer a phone kept offline from
// before Tripmo said which.
function MoneyCurrency({ trip }: { trip: TripView }) {
  if (trip.your_currency === undefined) return null;
  const yours = trip.your_currency;
  const estimate = trip.money.estimate;
  if (estimate && estimate.paid === null && estimate.booked_unpaid === null) return null;
  if (!estimate && yours) return null;
  const profile = (
    <a
      className="tm-link"
      href="/profile"
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate("/profile");
      }}
    >
      your profile
    </a>
  );
  let words: ReactNode;
  if (!estimate) words = <>Choose your currency on {profile} to see an estimate in it.</>;
  else if (yours?.from === "account")
    words = (
      <>
        Estimates are in {yours.currency}, your currency, chosen on {profile}.
      </>
    );
  else if (yours?.from === "trip")
    words =
      yours.account && yours.account !== yours.currency ? (
        <>
          Estimates are in {yours.currency}, set for this trip; your currency, {yours.account}, is on {profile}.
        </>
      ) : (
        <>Estimates are in {yours.currency}, set for this trip.</>
      );
  else
    words = (
      <>
        Estimates are in {estimate.currency}, the budget's: choose your currency on {profile}.
      </>
    );
  return <p className="caption tm-muted tm-money-currency">{words}</p>;
}
